export const MarketInsights = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Mixed-Use Market Insights
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Understanding current market dynamics helps you make informed decisions about selling your mixed-use property.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 text-center">
            <div className="text-5xl font-bold text-teal-600 mb-4">$3M-$15M</div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">Typical Value Range</h3>
            <p className="text-gray-600 leading-relaxed">
              Most mixed-use properties trade in this range, varying by size, location, and tenant mix.
            </p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 text-center">
            <div className="text-5xl font-bold text-teal-600 mb-4">5-8%</div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">Cap Rate Range</h3>
            <p className="text-gray-600 leading-relaxed">
              Cap rates vary based on location, tenant quality, and property condition.
            </p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 text-center">
            <div className="text-5xl font-bold text-teal-600 mb-4">120-240</div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">Days on Market</h3>
            <p className="text-gray-600 leading-relaxed">
              Traditional sales take 4-8 months. We can close in 30-45 days.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold mb-6 text-gray-900">Current Market Trends</h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <i className="ri-arrow-up-line text-teal-500 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Urban Revitalization</h4>
                  <p className="text-gray-600">Continued investment in downtown and main street mixed-use developments.</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-arrow-up-line text-teal-500 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Live-Work-Play Demand</h4>
                  <p className="text-gray-600">Strong demand for walkable, mixed-use environments from residents and businesses.</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-arrow-up-line text-teal-500 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Investor Interest</h4>
                  <p className="text-gray-600">Institutional and private investors actively seeking quality mixed-use properties.</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-arrow-right-line text-teal-500 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Retail Evolution</h4>
                  <p className="text-gray-600">Changing retail landscape creating repositioning opportunities.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/2505d6fd648561e360934a06441bceea.jpg"
                alt="Mixed-Use Market"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 md:p-12">
          <h3 className="text-3xl font-bold mb-8 text-gray-900">Why Now Is a Good Time to Sell</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Strong Demand:</strong> Active buyer pool for quality mixed-use properties</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Urban Focus:</strong> Continued investment in walkable, mixed-use environments</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Tax Planning:</strong> Strategic timing can optimize your tax situation</span>
                </li>
              </ul>
            </div>
            <div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Complexity Reduction:</strong> Simplify your portfolio and reduce management burden</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Capital Redeployment:</strong> Free up capital for other investment opportunities</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Market Timing:</strong> Take advantage of current market conditions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};